import { useEffect } from 'react';
import { Button, Flex, Title, Text, Switch } from '@mantine/core';
import {
  Form,
  FormMultiselect,
  FormRichTextEditor,
  FormTextarea,
  FormTextField,
} from '@shared/ui';
import { FormCompanySelect } from '@entities/company';
import { FormCitiesMultiSelect, useGetCities } from '@entities/region';
import { type VacancyEditMainInfoInitialValues } from './types';
import { useEditVacancy } from '@entities/vacancy';
import { showNotification } from '@shared/utils';
import { useParams } from 'react-router';
import { QueryKey } from '@shared/config';
import { useQueryClient } from '@tanstack/react-query';
import { VacancyCandidatesCount } from '../vacancyCandidatesCount/vacancyCandidatesCount';
import { FormActivitySphereMultiselect } from '@entities/activitySphere';
import { FormTagsInputSkills } from '@entities/skill/components/FormTagsInputSkills';
import { FormSalarySelectField } from '@widgets/form';
import { useGetScheduleWork } from '@entities/scheduleWork';
import { useGetEmploymentType } from '@entities/employmentType';
import { useGetSkill } from '@entities/skill';
import { useGetCurrency } from '@entities/currency';
import { useGetPaymentPeriod } from '@entities/paymentPeriod';
import { getTagsByGroupsForCreate } from '@shared/helpers';

import { useVacancyEditMainInfoFormContext } from './vacancyEditMainInfoFormContext';

type VacancyEditMainInfoProps = {
  handleCancel: () => void;
  /** Совпадает с `initialValues` у `VacancyEditMainInfoFormRoot` — для синхронизации селектов после подгрузки справочников */
  initialValues: VacancyEditMainInfoInitialValues;
};

export const VacancyEditMainInfo = ({
  handleCancel,
  initialValues,
}: VacancyEditMainInfoProps) => {
  const queryClient = useQueryClient();

  const { id } = useParams();

  const { isLoading: isRegionsLoading } = useGetCities();
  const { mutate: editVacancy, isPending: isEditVacancyPending } =
    useEditVacancy(id || '');

  const { data: scheduleWorks, isLoading: isScheduleWorksLoading } =
    useGetScheduleWork();
  const { data: employmentTypes, isLoading: isEmploymentTypesLoading } =
    useGetEmploymentType();
  const { data: skills, isLoading: isSkillsLoading } = useGetSkill();
  const { isLoading: isCurrenciesLoading } = useGetCurrency();
  const { isLoading: isPaymentPeriodsLoading } = useGetPaymentPeriod();

  const form = useVacancyEditMainInfoFormContext();

  const handleSubmit = (values: VacancyEditMainInfoInitialValues) => {
    editVacancy(
      {
        ...values,
        name: values.name,
        companyId: Number(values.companyId),
        cityIds: values.cityIds.map(Number),
        tariffId: Number(values.tariffId),
        activitySphereIds: values.activitySphereIds.map((id: string) =>
          Number(id)
        ),
        requiredCandidatesCount: Number(values.requiredCandidatesCount),
        currencyId: values.currencyId ? Number(values.currencyId) : 0,
        salaryFrom: values.salaryFrom || 0,
        salaryTo: values.salaryTo || 0,
        mandatoryRequirements: values.mandatoryRequirements,
        additionalRequirements: values.additionalRequirements,
        responsibilities: values.responsibilities,
        employmentTypeIds: values.employmentTypeIds.map((id: string) =>
          Number(id)
        ),
        paymentPeriodId: Number(values.paymentPeriodId),
        scheduleWorkIds: values.scheduleWorkIds.map((id: string) => Number(id)),
        skills:
          values.skills.length === 0
            ? null
            : getTagsByGroupsForCreate({
                directory: skills || [],
                selectedDirectory: values.skills,
              }),
        interviewFinalMessage: values.interviewFinalMessage ?? '',
      },
      {
        onSuccess: data => {
          queryClient.setQueryData([QueryKey.GET_VACANCY, Number(id)], data);

          handleCancel();
        },
        onError: error => {
          let message =
            error.response?.data.message || 'Не удалось сохранить вакансию';
          const errors = error.response?.data.data?.errors;

          if (errors?.['region.existing.id'] || errors?.['region.new.name']) {
            message = 'Нельзя выбрать несколько регионов';
          }

          showNotification({
            error,
            variant: 'error',
            message,
          });
        },
      }
    );
  };

  useEffect(() => {
    if (!isRegionsLoading && initialValues.cityIds.length > 0) {
      form.setFieldValue('cityIds', initialValues.cityIds);
    }
  }, [isRegionsLoading, initialValues.cityIds]);

  useEffect(() => {
    if (!isScheduleWorksLoading && initialValues.scheduleWorkIds.length > 0) {
      form.setFieldValue('scheduleWorkIds', initialValues.scheduleWorkIds);
    }
  }, [isScheduleWorksLoading, initialValues.scheduleWorkIds]);

  useEffect(() => {
    if (
      !isEmploymentTypesLoading &&
      initialValues.employmentTypeIds.length > 0
    ) {
      form.setFieldValue('employmentTypeIds', initialValues.employmentTypeIds);
    }
  }, [isEmploymentTypesLoading, initialValues.employmentTypeIds]);

  useEffect(() => {
    if (!isSkillsLoading && initialValues.skills.length > 0) {
      form.setFieldValue('skills', initialValues.skills);
    }
  }, [isSkillsLoading, initialValues.skills]);

  useEffect(() => {
    if (!isCurrenciesLoading && initialValues.currencyId) {
      form.setFieldValue('currencyId', initialValues.currencyId);
    }
  }, [isCurrenciesLoading, initialValues.currencyId]);

  useEffect(() => {
    if (!isPaymentPeriodsLoading && initialValues.paymentPeriodId.length > 0) {
      form.setFieldValue('paymentPeriodId', initialValues.paymentPeriodId);
    }
  }, [isPaymentPeriodsLoading, initialValues.paymentPeriodId]);

  const scheduleWorkOptions =
    scheduleWorks?.map((item: { id: number; name: string }) => ({
      value: item.id.toString(),
      label: item.name,
    })) || [];

  const employmentTypeOptions =
    employmentTypes?.map((item: { id: number; name: string }) => ({
      value: item.id.toString(),
      label: item.name,
    })) || [];

  const skillOptions =
    skills?.map((item: { id: number; name: string }) => ({
      value: item.id.toString(),
      label: item.name,
    })) || [];

  return (
    <Form onSubmit={form.onSubmit(handleSubmit)}>
      <Flex gap={24} direction='column'>
        <Title order={4}>Основная информация</Title>

        <Flex direction='column'>
          <FormCompanySelect
            title='Компания'
            description='Выберите компанию-нанимателя'
            placeholder='Выберите'
            containerProps={{
              pt: 0,
            }}
            key={form.key('companyId')}
            {...form.getInputProps('companyId')}
            error={form.errors.companyId}
          />

          <FormActivitySphereMultiselect
            title={
              <Flex
                justify='space-between'
                align='center'
                gap='md'
                wrap='nowrap'
                w='100%'
              >
                <Text
                  fw={600}
                  size='md'
                  c='var(--mantine-color-primary-7)'
                  style={{ flexShrink: 0 }}
                >
                  Профессия
                </Text>
                <Switch
                  label='Автозаполнение'
                  {...form.getInputProps('ai_sphere_fill', {
                    type: 'checkbox',
                  })}
                  onChange={e => {
                    form
                      .getInputProps('ai_sphere_fill')
                      .onChange(e.target.checked);
                    if (e.target.checked) {
                      form.getInputProps('activitySphereIds').onChange([]);
                    }
                  }}
                />
              </Flex>
            }
            description='Выберите все подходящие профессии для вакансии'
            placeholder='Выберите'
            key={form.key('activitySphereIds')}
            {...form.getInputProps('activitySphereIds')}
            disabled={form.values.ai_sphere_fill}
            error={form.errors.activitySphereIds}
          />

          <FormTextField
            title='Название вакансии'
            description='Введите название должности, на которую Синтия будет искать лучшего кандидата'
            placeholder='Введите название'
            key={form.key('name')}
            {...form.getInputProps('name')}
          />

          <FormRichTextEditor
            title='Обязанности по вакансии'
            description='Опишите обязанности по вакансии'
            placeholder='Введите описание'
            key={form.key('responsibilities')}
            {...form.getInputProps('responsibilities')}
            containerProps={{
              mb: 0,
            }}
          />

          <VacancyCandidatesCount
            key={form.key('requiredCandidatesCount')}
            {...form.getInputProps('requiredCandidatesCount')}
            error={form.errors.requiredCandidatesCount}
          />

          <FormCitiesMultiSelect
            title='Город'
            description='В каком городе предстоит работать'
            placeholder='Выберите'
            key={form.key('cityIds')}
            {...form.getInputProps('cityIds')}
            error={form.errors.cityIds}
          />

          <FormMultiselect
            title='Тип занятости'
            description='Выберите все подходящие варианты занятости сотрудника'
            placeholder='Выберите'
            data={employmentTypeOptions}
            key={form.key('employmentTypeIds')}
            {...form.getInputProps('employmentTypeIds')}
          />
          <FormMultiselect
            title='График работы'
            description='Выберите все подходящие варианты графика работы'
            placeholder='Выберите'
            data={scheduleWorkOptions}
            key={form.key('scheduleWorkIds')}
            {...form.getInputProps('scheduleWorkIds')}
          />

          <FormSalarySelectField
            title='Уровень дохода'
            description='Укажите предполагаемый уровень дохода для сотрудника'
            currencyId={
              form.values.currencyId
                ? String(form.values.currencyId)
                : undefined
            }
            salaryFrom={form.values.salaryFrom?.toString()}
            salaryTo={form.values.salaryTo?.toString()}
            paymentPeriodId={form.values.paymentPeriodId}
            onCurrencyChange={value =>
              form.setFieldValue('currencyId', value || '')
            }
            onSalaryFromChange={value =>
              form.setFieldValue(
                'salaryFrom',
                typeof value === 'number' ? value : 0
              )
            }
            onSalaryToChange={value =>
              form.setFieldValue(
                'salaryTo',
                typeof value === 'number' ? value : 0
              )
            }
            onPaymentPeriodChange={value =>
              form.setFieldValue('paymentPeriodId', value || '')
            }
            propsCurrencyInput={{ error: form.errors.currencyId }}
            propsFromInput={{ error: form.errors.salaryFrom }}
            propsToInput={{ error: form.errors.salaryTo }}
            propsPaymentPeriodInput={{ error: form.errors.paymentPeriodId }}
          />

          <FormRichTextEditor
            title='Обязательные требования к кандидату'
            description='Укажите самые важные требования к кандидату, на которые Синтия должна ориентироваться в выборе. Кандидатов, не соответствующих данным требованиям, Синтия не будет рассматривать.'
            placeholder='Введите описание'
            key={form.key('mandatoryRequirements')}
            {...form.getInputProps('mandatoryRequirements')}
          />

          <FormRichTextEditor
            title='Дополнительные требования к кандидату'
            description='Добавьте специальные требования, важные для вас, чтобы Синтия смогла уточнить их у кандидата.'
            placeholder='Введите описание'
            key={form.key('additionalRequirements')}
            {...form.getInputProps('additionalRequirements')}
          />

          <FormTagsInputSkills
            title='Ключевые навыки'
            description='Выберите все необходимые ключевые навыки сотрудника'
            placeholder='Выберите'
            data={skillOptions}
            key={form.key('skills')}
            {...form.getInputProps('skills')}
          />

          <FormTextarea
            title='Сообщение для заинтересовавшихся'
            description='Как только кандидат будет найден, Синтия свяжется с ним и предложит вашу вакансию. Если кандидат подтвердит готовность работать, Синтия вышлет финальное сообщение из данного поля'
            placeholder='Введите место встречи'
            key={form.key('interviewFinalMessage')}
            {...form.getInputProps('interviewFinalMessage')}
          />
        </Flex>

        <Flex gap={8} mt={16}>
          <Button variant='gray' onClick={handleCancel}>
            Назад
          </Button>
          <Button
            type='submit'
            loading={isEditVacancyPending}
            disabled={isEditVacancyPending}
          >
            Сохранить
          </Button>
        </Flex>
      </Flex>
    </Form>
  );
};
